/**
 * CSV utilities for CN61 data handling
 */

export interface Cn61RateData {
  direction: 'inbound' | 'outbound';
  format: 'P' | 'G' | 'E' | 'X';
  type: 'weight' | 'items';
  rate: number;
}

export interface Cn61CsvRow {
  do_from: string;
  do_to: string;
  year: string;
  product: string;
  rg: string;
  td_rate_kg: string;
  td_rate_item: string;
}

/**
 * Parse CSV string into CN61 rate data
 * @param csvString - The CSV string to parse
 * @param currentData - Current CN61 data for context
 * @param currentUserPpo - Current user's PPO
 * @param expectedYear - Expected year to validate against
 * @returns Array of CN61 rate data objects
 */
export function parseCn61Csv(csvString: string, currentData?: any, currentUserPpo?: string, expectedYear?: number): Cn61RateData[] {
  const lines = csvString.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV must have at least a header row and one data row');
  }

  const headers = lines[0].split(',').map(header => header.trim().toLowerCase());
  
  // Validate required headers
  const requiredHeaders = ['do_from', 'do_to', 'year', 'product', 'rg', 'td_rate_kg', 'td_rate_item'];
  const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
  
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
  }

  const data: Cn61RateData[] = [];
  const errors: string[] = [];

  function sanitizeNumericCell(cell: string): string {
    if (cell == null) return '';
    const trimmed = cell.trim();
    // Remove leading apostrophe used to force Excel text formatting
    if (trimmed.startsWith("'")) return trimmed.slice(1);
    // Handle Excel formula-style text wrapper: ="2.000"
    if (/^=\".*\"$/.test(trimmed)) {
      return trimmed.substring(2, trimmed.length - 1);
    }
    return trimmed;
  }

  function validateThreeDecimals(cell: string, fieldName: 'td_rate_kg' | 'td_rate_item', rowIdx: number): boolean {
    if (cell === '') return true; // allow empty as zero
    const threeDecimalPattern = /^\d+(\.\d{3})$/;
    if (!threeDecimalPattern.test(cell)) {
      errors.push(`Row ${rowIdx}: ${fieldName} must have exactly 3 decimal places (e.g., 2.000, 0.125). Got: ${cell}`);
      return false;
    }
    return true;
  }
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines
    
    const values = line.split(',').map(value => value.trim());
    
    if (values.length !== headers.length) {
      errors.push(`Row ${i + 1} has ${values.length} values but expected ${headers.length}`);
      continue; // Skip this row but continue validation
    }

    const row: any = {};
    headers.forEach((header, index) => {
      row[header] = values[index];
    });

    let hasRowError = false;

    // Validate year if expectedYear is provided
    if (expectedYear !== undefined) {
      const rowYear = parseInt(row.year, 10);
      if (isNaN(rowYear)) {
        errors.push(`Row ${i + 1}: Invalid year value '${row.year}'. Must be a valid number`);
        hasRowError = true;
      } else if (rowYear !== expectedYear) {
        errors.push(`Row ${i + 1}: Year mismatch. Expected ${expectedYear} but got ${rowYear}`);
        hasRowError = true;
      }
    }

    // Validate service
    // Removed for UPU CN61 (no service column)

    // Validate product (format) - accept both "P" and "Format P" formats
    const productUpper = row.product.toUpperCase();
    const normalizedProduct = productUpper.replace('FORMAT ', '');
    const validProducts = ['P', 'G', 'E', 'X', 'REGISTERED', 'INSURED', 'EXPRESS', 'M'];
    if (!validProducts.includes(normalizedProduct)) {
      errors.push(`Row ${i + 1}: Invalid product. Must be 'P', 'G', 'E', 'X', 'Format P', 'Format G', 'Format E', 'Format X', 'registered', 'insured', 'express', or 'M'`);
      hasRowError = true;
    }

    // Sanitize and validate 3-decimal format
    const sanitizedKg = sanitizeNumericCell(row.td_rate_kg);
    const sanitizedItem = sanitizeNumericCell(row.td_rate_item);
    
    const kgValid = validateThreeDecimals(sanitizedKg, 'td_rate_kg', i + 1);
    const itemValid = validateThreeDecimals(sanitizedItem, 'td_rate_item', i + 1);
    
    if (!kgValid || !itemValid) {
      hasRowError = true;
    }

    // Validate and convert rates - handle empty values
    const tdRateKg = sanitizedKg === '' || sanitizedKg === null || sanitizedKg === undefined 
      ? 0 
      : parseFloat(sanitizedKg);
    const tdRateItem = sanitizedItem === '' || sanitizedItem === null || sanitizedItem === undefined 
      ? 0 
      : parseFloat(sanitizedItem);
    
    if (isNaN(tdRateKg) || tdRateKg < 0) {
      errors.push(`Row ${i + 1}: Invalid td_rate_kg value. Must be a non-negative number`);
      hasRowError = true;
    }
    
    if (isNaN(tdRateItem) || tdRateItem < 0) {
      errors.push(`Row ${i + 1}: Invalid td_rate_item value. Must be a non-negative number`);
      hasRowError = true;
    }

    // Skip adding data if this row has errors
    if (hasRowError) {
      continue;
    }

    // Determine direction based on do_from and do_to
    // For inbound: do_from = filter DO (other PPO), do_to = current user DO (receiving from other)
    // For outbound: do_from = current user DO, do_to = filter DO (other PPO) (sending to other)
    const filterPpo = currentData?.ppo || 'UNKNOWN';
    const userPpo = currentUserPpo || filterPpo;
    
    // If do_from is the filter DO (other PPO), it's inbound; if do_from is the current user DO, it's outbound
    const direction = row.do_from === filterPpo ? 'inbound' : 'outbound';

    // Handle different product types - normalize by removing "Format " prefix
    const product = row.product.toUpperCase().replace('FORMAT ', '');
    
    // For P, G, E, X formats - add both weight and items if provided
    if (['P', 'G', 'E', 'X'].includes(product)) {
      // Add weight data (including zero values)
      data.push({
        direction: direction as 'inbound' | 'outbound',
        format: product as 'P' | 'G' | 'E' | 'X',
        type: 'weight' as 'weight' | 'items',
        rate: tdRateKg
      });
      
      // Add items data (including zero values)
      data.push({
        direction: direction as 'inbound' | 'outbound',
        format: product as 'P' | 'G' | 'E' | 'X',
        type: 'items' as 'weight' | 'items',
        rate: tdRateItem
      });
    } else {
      // For registered, insured, express, M - handle as special cases
      // Map to the actual field names in the data structure
      const specialFormat = product === 'M' ? 'mbagsWeight' : 
                           product === 'REGISTERED' ? 'registeredItems' :
                           product === 'INSURED' ? 'insuredItems' :
                           product === 'EXPRESS' ? 'expressItems' : product;
      
      // For mbagsWeight (M), only use weight rate, not items rate
      if (product === 'M') {
        if (tdRateKg >= 0) {
          data.push({
            direction: direction as 'inbound' | 'outbound',
            format: specialFormat as any,
            type: 'weight' as 'weight' | 'items',
            rate: tdRateKg
          });
        }
      } else {
        // For other special fields (registered, insured, express), add both weight and items if they exist
        if (tdRateKg >= 0) {
          data.push({
            direction: direction as 'inbound' | 'outbound',
            format: specialFormat as any,
            type: 'weight' as 'weight' | 'items',
            rate: tdRateKg
          });
        }
        if (tdRateItem >= 0 && tdRateKg !== tdRateItem) {
          data.push({
            direction: direction as 'inbound' | 'outbound',
            format: specialFormat as any,
            type: 'items' as 'weight' | 'items',
            rate: tdRateItem
          });
        }
      }
    }
  }

  // If there were any errors during validation, throw them all
  if (errors.length > 0) {
    throw new Error(errors.join('|||')); // Use ||| as delimiter to split later
  }

  return data;
}

/**
 * Convert CN61 data to CSV format
 * @param data - CN61 data object
 * @returns CSV string
 */
export function convertCn61DataToCsv(data: any, currentUserPpo?: string): string {
  if (!data) {
    return 'do_from,do_to,year,product,rg,td_rate_kg,td_rate_item\n';
  }

  const csvRows = ['do_from,do_to,year,product,rg,td_rate_kg,td_rate_item'];
  
  const filterPpo = data.ppo || 'UNKNOWN'; // The DO being processed (from filter)
  const year = data.year || new Date().getFullYear();
  const userPpo = currentUserPpo || filterPpo; // Current user's DO

  // Format value to exactly 3 decimals for CSV content
  function formatTo3Decimals(value: any): string {
    if (value === null || value === undefined || value === '') return '0.000';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0.000';
    return num.toFixed(3);
  }

  function formatCsvValueForExport(value: any): string {
    return formatTo3Decimals(value);
  }
  
  // Process inbound data
  if (data.inboundRates) {
    const inbound = data.inboundRates;
    
    // Process each format
    ['formatP', 'formatG', 'formatE', 'formatX'].forEach(formatKey => {
      const format = inbound[formatKey];
      if (format) {
        const product = `Format ${formatKey.replace('format', '')}`;
        const weightRate = format.weight?.rate || 0;
        const itemRate = format.items?.rate || 0;
        
        // For inbound: do_from = filter DO (other PPO), do_to = current user DO (receiving from other)
        const weightValue = formatCsvValueForExport(weightRate);
        const itemValue = formatCsvValueForExport(itemRate);
        csvRows.push(`${filterPpo},${userPpo},${year},${product},UPU,${weightValue},${itemValue}`);
      }
    });
    
    // Process other fields
    ['registeredItems', 'insuredItems', 'expressItems', 'mbagsWeight'].forEach(fieldKey => {
      const field = inbound[fieldKey];
      if (field?.rate !== undefined && field.rate !== null && field.rate > 0) {
        const product = fieldKey === 'mbagsWeight' ? 'M' : fieldKey.replace('Items', '');
        const weightRate = fieldKey === 'mbagsWeight' ? field.rate : 0;
        const itemRate = fieldKey === 'mbagsWeight' ? 0 : field.rate;
        // For inbound: do_from = filter DO (other PPO), do_to = current user DO (receiving from other)
        const weightValue = formatCsvValueForExport(weightRate);
        const itemValue = formatCsvValueForExport(itemRate);
        csvRows.push(`${filterPpo},${userPpo},${year},${product},UPU,${weightValue},${itemValue}`);
      }
    });
  }
  
  // Process outbound data
  if (data.outboundRates) {
    const outbound = data.outboundRates;
    
    // Process each format
    ['formatP', 'formatG', 'formatE', 'formatX'].forEach(formatKey => {
      const format = outbound[formatKey];
      if (format) {
        const product = `Format ${formatKey.replace('format', '')}`;
        const weightRate = format.weight?.rate || 0;
        const itemRate = format.items?.rate || 0;
        
        // For outbound: do_from = current user DO, do_to = filter DO (other PPO) (sending to other)
        const weightValue = formatCsvValueForExport(weightRate);
        const itemValue = formatCsvValueForExport(itemRate);
        csvRows.push(`${userPpo},${filterPpo},${year},${product},UPU,${weightValue},${itemValue}`);
      }
    });
    
    // Process other fields
    ['registeredItems', 'insuredItems', 'expressItems', 'mbagsWeight'].forEach(fieldKey => {
      const field = outbound[fieldKey];
      if (field?.rate !== undefined && field.rate !== null && field.rate > 0) {
        const product = fieldKey === 'mbagsWeight' ? 'M' : fieldKey.replace('Items', '');
        const weightRate = fieldKey === 'mbagsWeight' ? field.rate : 0;
        const itemRate = fieldKey === 'mbagsWeight' ? 0 : field.rate;
        // For outbound: do_from = current user DO, do_to = filter DO (other PPO) (sending to other)
        const weightValue = formatCsvValueForExport(weightRate);
        const itemValue = formatCsvValueForExport(itemRate);
        csvRows.push(`${userPpo},${filterPpo},${year},${product},UPU,${weightValue},${itemValue}`);
      }
    });
  }

  return csvRows.join('\n');
}

/**
 * Apply CSV data to CN61 data structure
 * @param csvData - Parsed CSV data
 * @param baseData - Base CN61 data structure
 * @returns Updated CN61 data
 */
export function applyCsvDataToCn61(csvData: Cn61RateData[], baseData: any): any {
  console.log('applyCsvDataToCn61 called with:', { csvData, baseData });
  
  const updatedData = { ...baseData };
  
  // Helper to format rate to exactly 3 decimals
  const formatRate = (rate: number): string => {
    return rate.toFixed(3);
  };
  
  // Only initialize structures for directions that have data
  const hasInboundData = csvData.some(row => row.direction === 'inbound');
  const hasOutboundData = csvData.some(row => row.direction === 'outbound');
  
  if (hasInboundData && !updatedData.inboundRates) {
    updatedData.inboundRates = {};
  }
  if (hasOutboundData && !updatedData.outboundRates) {
    updatedData.outboundRates = {};
  }
  
  csvData.forEach((row, index) => {
    console.log(`Processing row ${index}:`, row);
    const rates = row.direction === 'inbound' ? updatedData.inboundRates : updatedData.outboundRates;
    
    if (['P', 'G', 'E', 'X'].includes(row.format)) {
      // Handle format fields
      const formatKey = `format${row.format}`;
      console.log(`Processing format field: ${formatKey}`);
      if (!rates[formatKey]) {
        rates[formatKey] = {};
      }
      if (!rates[formatKey][row.type]) {
        rates[formatKey][row.type] = {};
      }
      rates[formatKey][row.type].rate = formatRate(row.rate);
      console.log(`Set ${formatKey}.${row.type}.rate = ${formatRate(row.rate)}`);
    } else {
      // Handle special fields (mbagsWeight, registeredItems, insuredItems, expressItems)
      const fieldKey = row.format as string;
      console.log(`Processing special field: ${fieldKey}`);
      
      // For mbagsWeight, only process weight entries (ignore items entries)
      if (fieldKey === 'mbagsWeight' && row.type === 'items') {
        console.log(`Skipping items entry for mbagsWeight (weight-only field)`);
        return; // Skip items entries for mbagsWeight
      }
      
      if (!rates[fieldKey]) {
        rates[fieldKey] = {};
      }
      rates[fieldKey].rate = formatRate(row.rate);
      console.log(`Set ${fieldKey}.rate = ${formatRate(row.rate)}`);
    }
  });
  
  console.log('Final updated data:', updatedData);
  return updatedData;
}

/**
 * Download CSV file
 * @param csvContent - CSV string content
 * @param filename - Name of the file to download
 */
export function downloadCn61Csv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Generate sample CSV data for CN61
 * @param ppo - The PPO code to use
 * @param year - The year
 * @returns Sample CSV string
 */
export function generateCn61SampleCsv(ppo: string, year: string): string {
  const sampleData = [
    'do_from,do_to,year,product,rg,td_rate_kg,td_rate_item',
    `${ppo},${ppo},${year},Format P,UPU,0.500,1.000`,
    `${ppo},${ppo},${year},Format G,UPU,0.300,0.800`,
    `${ppo},${ppo},${year},Format E,UPU,0.200,0.500`,
    `${ppo},${ppo},${year},Format X,UPU,0.100,0.300`,
    `${ppo},${ppo},${year},registered,UPU,0.000,2.000`,
    `${ppo},${ppo},${year},insured,UPU,0.000,1.500`,
    `${ppo},${ppo},${year},express,UPU,0.000,3.000`,
    `${ppo},${ppo},${year},M,UPU,0.100,0.000`,
    `${ppo},OTHER,${year},Format P,UPU,0.400,0.900`,
    `${ppo},OTHER,${year},Format G,UPU,0.250,0.700`,
    `${ppo},OTHER,${year},Format E,UPU,0.150,0.400`,
    `${ppo},OTHER,${year},Format X,UPU,0.080,0.200`,
    `${ppo},OTHER,${year},registered,UPU,0.000,1.800`,
    `${ppo},OTHER,${year},insured,UPU,0.000,1.300`,
    `${ppo},OTHER,${year},express,UPU,0.000,2.800`,
    `${ppo},OTHER,${year},M,UPU,0.080,0.000`
  ];
  
  return sampleData.join('\n');
}
